import { useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { DatePickerHeader } from '../DatePicker/DatePickerHeader'
import './style.css'

import de from 'date-fns/locale/de'
import en from 'date-fns/locale/en-GB'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import hr from 'date-fns/locale/hr'
import it from 'date-fns/locale/it'
import pt from 'date-fns/locale/pt'
import { Button } from '../Button'

const locales: Record<string, Locale> = { 'en-GB': en, hr, fr, de, it, es, pt }
Object.entries(locales).forEach(([key, locale]) => registerLocale(key, locale))

interface CalendarFilterModalContentProps {
    onClose: () => void
    onDateSelect: (date: Date | null) => void
    selectedDate: Date | null
    locale?: string
}

export const CalendarFilterModalContent = ({
    onClose,
    onDateSelect,
    selectedDate,
    locale = 'hr',
}: CalendarFilterModalContentProps) => {
    const { t } = useTranslation(['common', 'recreation'])

    const [tempDate, setTempDate] = useState<Date | null>(selectedDate)
    const [openToDate, setOpenToDate] = useState<Date>(new Date())
    const [calendarKey, setCalendarKey] = useState(0)

    const handleDateChange = (date: Date | null) => {
        setTempDate(date)
    }

    const handleApply = () => {
        onDateSelect(tempDate)
        onClose()
    }

    const handleClear = () => {
        setTempDate(null)
        onDateSelect(null)
        onClose()
    }

    const selectedLocale = locales[locale] ? locale : 'hr'

    return (
        <div className="m-0 flex h-full w-full flex-col p-0">
            <div className="m-0 h-full w-full p-0">
                <div className="m-0 w-full p-0">
                    <ReactDatePicker
                        key={calendarKey}
                        inline
                        selected={tempDate}
                        onChange={handleDateChange}
                        dateFormat="dd.MM.yyyy"
                        locale={selectedLocale}
                        showTimeSelect={false}
                        minDate={null}
                        calendarClassName="w-full"
                        openToDate={openToDate}
                        preventOpenOnFocus={true}
                        shouldCloseOnSelect={false}
                        renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            changeMonth,
                            changeYear,
                        }) => (
                            <DatePickerHeader
                                className="m-0 w-full p-0"
                                date={date}
                                decreaseMonth={() => {
                                    const newDate = new Date(openToDate)
                                    newDate.setMonth(newDate.getMonth() - 1)
                                    setOpenToDate(newDate)
                                    setCalendarKey((prev) => prev + 1)
                                    decreaseMonth()
                                }}
                                increaseMonth={() => {
                                    const newDate = new Date(openToDate)
                                    newDate.setMonth(newDate.getMonth() + 1)
                                    setOpenToDate(newDate)
                                    setCalendarKey((prev) => prev + 1)
                                    increaseMonth()
                                }}
                                changeMonth={(month) => {
                                    const newDate = new Date(openToDate)
                                    newDate.setMonth(month)
                                    setOpenToDate(newDate)
                                    setCalendarKey((prev) => prev + 1)
                                    changeMonth(month)
                                }}
                                changeYear={(year) => {
                                    const newDate = new Date(openToDate)
                                    newDate.setFullYear(year)
                                    setOpenToDate(newDate)
                                    setCalendarKey((prev) => prev + 1)
                                    changeYear(year)
                                }}
                                t={t}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="flex justify-end gap-4 px-4 py-2">
                <Button
                    onClick={handleClear}
                    label={t('actions.cancel')}
                    variant="contained"
                    color="light"
                    size="base"
                />
                <Button
                    onClick={handleApply}
                    label={t('actions.show')}
                    variant="contained"
                    color="primary"
                    size="base"
                />
            </div>
        </div>
    )
}
